import type { PrintableColor } from '../types/assets.js';

// Print-color options for the designer's "Select Colors" wizard step
// (BackgroundColorTool.tsx). An admin configures, per product, which of these
// are offered; a `?printColors=` URL param can further narrow that set at
// launch time (e.g. a specific storefront placement that only supports a
// subset). 'full-color' means "skip quantization entirely — pass every color
// in the design through to print unchanged"; a number means "flatten to at
// most N flat colors", same as the original hardcoded 1-4 behavior.
export const FULL_COLOR = 'full-color' as const;
export type PrintColorOption = typeof FULL_COLOR | number;

export const MAX_SPOT_COLORS = 16;

function hexToRgbTuple(hex: string): [number, number, number] {
  const h = hex.startsWith('#') ? hex : `#${hex}`;
  return [
    parseInt(h.slice(1, 3), 16) || 0,
    parseInt(h.slice(3, 5), 16) || 0,
    parseInt(h.slice(5, 7), 16) || 0,
  ];
}

// Nearest admin-configured PrintableColor to `hex` by plain Euclidean RGB
// distance — this app has no live CMYK->PMS conversion service, so "closest
// PMS" is resolved against the admin's own imported color library
// (PrintableColor.pantoneColor, set via the CSV import — see
// backend/src/routes/admin/colors.ts) rather than computed from scratch.
// Returns null when the library has no PMS-tagged entries at all, or none
// within MAX_DISTANCE of this hex — an unrelated PMS code would be actively
// misleading on a print spec. Shared across the frontend designer
// (PantoneMatch.tsx's swatch editors), the Design Guide PDF
// (designGuideService.ts), and the admin Order Detail page — all three need
// the identical "what PMS code is this color closest to" answer against the
// same admin-maintained library, so this lives here rather than being
// duplicated per consumer.
const MAX_PMS_DISTANCE = 60; // ~each channel within ~35 of target, in combined RGB distance

export function nearestPantone(hex: string, printableColors: PrintableColor[]): PrintableColor | null {
  const tagged = printableColors.filter(c => c.pantoneColor);
  if (tagged.length === 0) return null;
  const [r, g, b] = hexToRgbTuple(hex);
  let best: PrintableColor | null = null;
  let bestDist = Infinity;
  for (const c of tagged) {
    const [cr, cg, cb] = hexToRgbTuple(c.colorCode);
    const dist = Math.sqrt((r - cr) ** 2 + (g - cg) ** 2 + (b - cb) ** 2);
    if (dist < bestDist) { bestDist = dist; best = c; }
  }
  return bestDist <= MAX_PMS_DISTANCE ? best : null;
}

// All choices an admin can pick from when configuring a product — Full Color
// plus 1 through MAX_SPOT_COLORS.
export const ALL_PRINT_COLOR_OPTIONS: PrintColorOption[] = [
  FULL_COLOR,
  ...Array.from({ length: MAX_SPOT_COLORS }, (_, i) => i + 1),
];

// Pre-existing products (or a product an admin hasn't touched this setting
// on yet) have no `printColorOptions` saved — falls back to the original
// fixed 1-4 behavior rather than suddenly exposing 1-10 + Full Color.
export const DEFAULT_PRINT_COLOR_OPTIONS: PrintColorOption[] = [1, 2, 3, 4];

export function printColorOptionToString(opt: PrintColorOption): string {
  return opt === FULL_COLOR ? FULL_COLOR : String(opt);
}

// Also parses Dutch vendor-style free-text color-count labels used directly
// on Magento's own custom-option values (e.g. PX120181's Zeefdruk/Transfer
// tiers: "1 kleur", "2 kleuren", "3 kleuren") — these ARE genuine flat
// color counts, just spelled out rather than a bare integer, so the same
// PrintColorOption model applies. Distinct from other vendor tier labels
// (e.g. DTF Transfer's "max 100 cm2") which represent PRINT AREA SIZE, not
// color count, and deliberately do NOT parse here — those correctly fall
// through to null (same as "None"), so no print-color picker is shown for
// a method whose tiers aren't actually about color at all.
const KLEUR_PATTERN = /^(\d+)\s*kleur(en)?$/;

export function printColorOptionFromString(raw: string): PrintColorOption | null {
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) return null;
  if (trimmed === FULL_COLOR) return FULL_COLOR;
  const kleurMatch = trimmed.match(KLEUR_PATTERN);
  if (kleurMatch) {
    const n = Number(kleurMatch[1]);
    return Number.isInteger(n) && n >= 1 && n <= MAX_SPOT_COLORS ? n : null;
  }
  const n = Number(trimmed);
  return Number.isInteger(n) && n >= 1 && n <= MAX_SPOT_COLORS ? n : null;
}

// Admin-facing serialize/parse for the comma-separated `printColorOptions`
// DB column (ProductConfig.printColorOptions) — same convention as this
// model's existing `showCutLineOn` field.
export function serializePrintColorOptions(options: PrintColorOption[]): string {
  return options.map(printColorOptionToString).join(',');
}

export function parsePrintColorOptions(raw: string | null | undefined): PrintColorOption[] {
  if (!raw) return DEFAULT_PRINT_COLOR_OPTIONS;
  const parsed = raw.split(',').map(printColorOptionFromString).filter((o): o is PrintColorOption => o !== null);
  return parsed.length ? parsed : DEFAULT_PRINT_COLOR_OPTIONS;
}

// Builds a ProductConfig.printColorOptions string from a vendor's raw
// per-print-area color-count values (e.g. PFConcept's maxColours, TopPoint's
// numberOfColors, Clipper/GivingEurope's maxColorQuantity). Each vendor field
// is only ever a MAX COUNT, never actual color names/hex values (confirmed
// absent from all four onboarded vendor feeds as of 2026-07-10) -- so this
// expands "N" into the full "1,2,...,N" selectable range the admin's "Select
// Colors" wizard step already expects an admin to narrow down from, per the
// user's direction: "maxColorQuantity is 5 then we'll select 1 to 5 in
// admin". A raw value of "Full Colour"/"full color" (TopPoint's literal
// string for full-color methods) contributes FULL_COLOR instead of a number.
// Non-numeric, non-"full colour" values are dropped rather than throwing,
// since this runs unattended over large vendor feeds. A count ABOVE
// MAX_SPOT_COLORS is clamped to the cap, not dropped -- confirmed present in
// real feeds (PFConcept/Clipper/GivingEurope all have genuine 12-color print
// areas; Clipper also has 6/9/10) and dropping it entirely used to silently
// fall back to the unrelated DEFAULT_PRINT_COLOR_OPTIONS (1-4), understating
// a real 12-color product as a 4-color one. Returns null (not an empty
// string) only when NO usable value was found at all, so callers can
// distinguish "no vendor color data" from "leave unset".
export function printColorOptionsFromVendorCounts(
  rawCounts: Array<string | number | null | undefined>,
): string | null {
  let maxCount = 0;
  let hasFullColor = false;
  for (const raw of rawCounts) {
    if (raw === null || raw === undefined) continue;
    const trimmed = String(raw).trim().toLowerCase();
    if (!trimmed) continue;
    if (trimmed === 'full colour' || trimmed === 'full color' || trimmed === FULL_COLOR) {
      hasFullColor = true;
      continue;
    }
    const n = Number(trimmed);
    if (Number.isInteger(n) && n >= 1) {
      maxCount = Math.max(maxCount, Math.min(n, MAX_SPOT_COLORS));
    }
  }
  if (!hasFullColor && maxCount === 0) return null;
  const options: PrintColorOption[] = hasFullColor ? [FULL_COLOR] : [];
  for (let i = 1; i <= maxCount; i++) options.push(i);
  return serializePrintColorOptions(options);
}

// Narrows `adminOptions` down to whatever subset `requested` (already parsed
// from e.g. a `?printColors=` URL param — see useProductSetup.ts's
// getRequestedPrintColorOptionsFromSearch) also appears in — the admin's own
// set is always the ceiling, a value not present there can't be smuggled in
// via the URL. Empty/fully-non-matching `requested` -> null, meaning "no
// override, use the admin set as-is".
export function filterPrintColorOptions(
  requested: PrintColorOption[],
  adminOptions: PrintColorOption[],
): PrintColorOption[] | null {
  const allowed = requested.filter(r => adminOptions.some(a => printColorOptionToString(a) === printColorOptionToString(r)));
  return allowed.length ? allowed : null;
}
